import { useEffect, useMemo, useState } from 'react'
import { CalendarDays, BarChart3 } from 'lucide-react'
import { apiGet, cachedGet } from '../../lib/api.js'
import { usePersistedState } from '../../lib/persist.js'
import RevenueChart from '../../components/RevenueChart.jsx'
import RadarChart from '../../components/RadarChart.jsx'
import HeatmapYM from '../../components/HeatmapYM.jsx'
import {
  TH_MON_ABBR, TH_MON_FULL, MONTH_START, DAILY_LIMIT, ORANGE, BLUE, GREEN, PURPLE, SLATE, PALETTE, RADAR_PRESETS,
  pad, toISO, daysBetween, fmt, enumMonths, bucketsFor, presetRange, comparePrevRange, compareLabel, comparePrevLabel, asList,
  Card, Segmented, Toggle, PresetBar, DateRange, FilterChips, Stat, ChartLoading, Notice, todayISO,
} from './statShared.jsx'

// Whitening = Zoom + Cool Light. The new-patient side is one combined campaign
// (stat_type=whitening), but revenue is split by treatment type: zoom / coollight.
// Top chart shows the two types side by side; the all-time chart sums them.
const ZOOM = BLUE
const COOL = ORANGE
const TYPES = [
  { key: 'zoom', type: 'zoom', label: 'Zoom', color: ZOOM },
  { key: 'cool', type: 'coollight', label: 'Cool Light', color: COOL },
]
const DATA_START = `${Number(todayISO().slice(0, 4)) - 10}-01-01` // heatmap spans up to a decade
const TOOTHTAXI_BRANCH = '9' // mobile unit — off by default
const rowKey = (gran, r) => (gran === 'month' ? `${r.year}-${pad(r.month)}` : r.date)

export default function WhiteningRevenueStat() {
  const today = useMemo(() => todayISO(), [])  // Bangkok, not the browser clock
  const [branchName, setBranchName] = useState({})

  const pfx = 'stat:whitening'
  const init = useMemo(() => presetRange('1mo'), [])
  const [start, setStart] = usePersistedState(`${pfx}:start`, init[0])
  const [end, setEnd] = usePersistedState(`${pfx}:end`, init[1])
  const [preset, setPreset] = usePersistedState(`${pfx}:preset`, '1mo')
  const [metric, setMetric] = usePersistedState(`${pfx}:metric`, 'clinic')   // total | clinic
  const [compareOn, setCompareOn] = usePersistedState(`${pfx}:compareOn`, false)
  const [branchOff, setBranchOff] = usePersistedState(`${pfx}:branchOff2`, () => new Set([TOOTHTAXI_BRANCH]))

  const [zoomTop, setZoomTop] = useState(null)
  const [coolTop, setCoolTop] = useState(null)
  const [topState, setTopState] = useState({ loading: true, err: '' })
  const [loadedGran, setLoadedGran] = useState(null)

  const [zoomCmp, setZoomCmp] = useState(null)
  const [coolCmp, setCoolCmp] = useState(null)
  const [cmpState, setCmpState] = useState({ loading: false, err: '' })
  const [cmpLoadedGran, setCmpLoadedGran] = useState(null)

  const [zoomMonth, setZoomMonth] = useState(null)
  const [coolMonth, setCoolMonth] = useState(null)
  const [monthState, setMonthState] = useState({ loading: true, err: '' })

  const [bottomView, setBottomView] = usePersistedState(`${pfx}:bottomView`, 'line')   // line | heatmap
  const [bottomType, setBottomType] = usePersistedState(`${pfx}:bottomType`, 'total')  // total (zoom+cool) | zoom | cool
  const [bottomLineOff, setBottomLineOff] = usePersistedState(`${pfx}:bottomLineOff`, () => new Set(['total']))
  const [heatMetric, setHeatMetric] = usePersistedState(`${pfx}:heatMetric`, 'clinic') // total | clinic | doctor

  const rangeInvalid = end < start
  const rangeDays = daysBetween(start, end) + 1
  const gran = rangeDays > DAILY_LIMIT ? 'month' : 'day'
  const endpoint = gran === 'month' ? '/api/revenue/monthly' : '/api/revenue/daily'
  const isRadar = RADAR_PRESETS.includes(preset)
  const wantCompare = compareOn && !rangeInvalid
  const topReady = zoomTop != null && coolTop != null && loadedGran === gran
  const cmpReady = zoomCmp != null && coolCmp != null && cmpLoadedGran === gran

  const metricKey = metric === 'total' ? 'priceTotal' : 'priceClinic'
  const metricLabel = metric === 'total' ? 'รวมทั้งหมด' : 'ส่วนคลินิก'

  const applyPreset = (key) => { const [s, e] = presetRange(key); setStart(s); setEnd(e); setPreset(key) }

  useEffect(() => {
    cachedGet('/api/branches')
      .then((br) => setBranchName(Object.fromEntries(asList(br).map((b) => [String(b.id ?? b.branch_id), b.name || b.branch_name || `สาขา #${b.id}`]))))
      .catch(() => {})
  }, [])

  // top range — zoom + cool
  useEffect(() => {
    if (rangeInvalid) return
    let alive = true
    setTopState({ loading: true, err: '' })
    Promise.all(TYPES.map((t) => apiGet(endpoint, { start_date: start, end_date: end, branch_id: 0, exclude_nhso: false, type: t.type })))
      .then(([z, c]) => { if (alive) { setZoomTop(Array.isArray(z) ? z : []); setCoolTop(Array.isArray(c) ? c : []); setLoadedGran(gran); setTopState({ loading: false, err: '' }) } })
      .catch((e) => { if (alive) { setZoomTop([]); setCoolTop([]); setLoadedGran(gran); setTopState({ loading: false, err: e.message || 'โหลดข้อมูลไม่สำเร็จ' }) } })
    return () => { alive = false }
  }, [endpoint, start, end, rangeInvalid, gran])

  // compare range — zoom + cool
  useEffect(() => {
    if (!wantCompare) { setZoomCmp(null); setCoolCmp(null); return }
    let alive = true
    const [ps, pe] = comparePrevRange(preset, start, end)
    setCmpState({ loading: true, err: '' })
    Promise.all(TYPES.map((t) => apiGet(endpoint, { start_date: ps, end_date: pe, branch_id: 0, exclude_nhso: false, type: t.type })))
      .then(([z, c]) => { if (alive) { setZoomCmp(Array.isArray(z) ? z : []); setCoolCmp(Array.isArray(c) ? c : []); setCmpLoadedGran(gran); setCmpState({ loading: false, err: '' }) } })
      .catch(() => { if (alive) { setZoomCmp([]); setCoolCmp([]); setCmpLoadedGran(gran); setCmpState({ loading: false, err: '' }) } })
    return () => { alive = false }
  }, [wantCompare, preset, endpoint, start, end, gran])

  // all-time monthly — zoom + cool (combined in the bottom chart)
  useEffect(() => {
    let alive = true
    setMonthState({ loading: true, err: '' })
    Promise.all(TYPES.map((t) => apiGet('/api/revenue/monthly', { start_date: DATA_START, end_date: today, branch_id: 0, exclude_nhso: false, type: t.type })))
      .then(([z, c]) => { if (alive) { setZoomMonth(Array.isArray(z) ? z : []); setCoolMonth(Array.isArray(c) ? c : []); setMonthState({ loading: false, err: '' }) } })
      .catch((e) => { if (alive) { setZoomMonth([]); setCoolMonth([]); setMonthState({ loading: false, err: e.message || 'โหลดข้อมูลไม่สำเร็จ' }) } })
    return () => { alive = false }
  }, [today])

  const branches = useMemo(() => {
    const ids = new Set()
    for (const arr of [zoomTop, coolTop, zoomMonth, coolMonth, zoomCmp, coolCmp]) for (const r of arr || []) ids.add(String(r.branch))
    return [...ids].filter(Boolean).sort((a, b) => Number(a) - Number(b))
      .map((id, i) => ({ id, label: branchName[id] || `สาขา #${id}`, color: PALETTE[i % PALETTE.length] }))
  }, [zoomTop, coolTop, zoomMonth, coolMonth, zoomCmp, coolCmp, branchName])
  const branchEnabled = useMemo(() => new Set(branches.filter((b) => !branchOff.has(b.id)).map((b) => b.id)), [branches, branchOff])
  const effectiveEnabled = branchEnabled // star chart respects the branch chips too

  const sumByBucket = (rows) => {
    const acc = {}
    for (const r of rows || []) { if (!effectiveEnabled.has(String(r.branch))) continue; const k = rowKey(gran, r); acc[k] = (acc[k] || 0) + (r[metricKey] || 0) }
    return acc
  }
  const totalOf = (rows) => (rows || []).reduce((s, r) => (effectiveEnabled.has(String(r.branch)) ? s + (r[metricKey] || 0) : s), 0)

  // top line chart (long ranges): Zoom + Cool Light, plus dashed previous when comparing
  const topLine = useMemo(() => {
    if (isRadar || rangeInvalid || !topReady) return { data: [], series: [] }
    const buckets = bucketsFor(gran, start, end)
    const z = sumByBucket(zoomTop), c = sumByBucket(coolTop)
    const series = [{ key: 'zoom', label: 'Zoom', color: ZOOM }, { key: 'cool', label: 'Cool Light', color: COOL }]
    let zp = {}, cp = {}, prevBuckets = []
    if (wantCompare) {
      const [ps, pe] = comparePrevRange(preset, start, end)
      prevBuckets = bucketsFor(gran, ps, pe)
      zp = sumByBucket(cmpReady ? zoomCmp : null); cp = sumByBucket(cmpReady ? coolCmp : null)
      series.push({ key: 'zoomPrev', label: `Zoom (${comparePrevLabel(preset)})`, color: ZOOM, dashed: true })
      series.push({ key: 'coolPrev', label: `Cool Light (${comparePrevLabel(preset)})`, color: COOL, dashed: true })
    }
    const data = buckets.map((bk, i) => {
      const row = { label: bk.label, full: bk.full, zoom: z[bk.key] || 0, cool: c[bk.key] || 0 }
      if (wantCompare) { row.zoomPrev = zp[prevBuckets[i]?.key] || 0; row.coolPrev = cp[prevBuckets[i]?.key] || 0 }
      return row
    })
    return { data, series }
  }, [isRadar, rangeInvalid, topReady, gran, start, end, preset, wantCompare, cmpReady, zoomTop, coolTop, zoomCmp, coolCmp, effectiveEnabled, metricKey])

  // star charts: one radar per type, branch on the spokes
  const radarOf = (rows, cmpRows) => {
    const cur = {}, prv = {}
    for (const r of rows || []) { const b = String(r.branch); cur[b] = (cur[b] || 0) + (r[metricKey] || 0) }
    if (wantCompare && cmpReady) for (const r of cmpRows || []) { const b = String(r.branch); prv[b] = (prv[b] || 0) + (r[metricKey] || 0) }
    return branches.filter((b) => branchEnabled.has(b.id))
      .map((b) => ({ key: b.id, label: b.label, color: b.color, value: cur[b.id] || 0, prev: prv[b.id] || 0 }))
      .filter((a) => a.value > 0 || (wantCompare && a.prev > 0))
  }
  const zoomRadar = useMemo(() => (isRadar && topReady ? radarOf(zoomTop, zoomCmp) : []), [isRadar, topReady, zoomTop, zoomCmp, wantCompare, cmpReady, branches, branchEnabled, metricKey])
  const coolRadar = useMemo(() => (isRadar && topReady ? radarOf(coolTop, coolCmp) : []), [isRadar, topReady, coolTop, coolCmp, wantCompare, cmpReady, branches, branchEnabled, metricKey])

  const zoomTotal = useMemo(() => totalOf(zoomTop), [zoomTop, effectiveEnabled, metricKey])
  const coolTotal = useMemo(() => totalOf(coolTop), [coolTop, effectiveEnabled, metricKey])
  const zoomPrevTotal = useMemo(() => totalOf(zoomCmp), [zoomCmp, effectiveEnabled, metricKey])
  const coolPrevTotal = useMemo(() => totalOf(coolCmp), [coolCmp, effectiveEnabled, metricKey])

  // rows for the bottom chart follow the technique selector (รวม / Zoom / Cool Light)
  const bottomMonthRows = useMemo(() => (
    bottomType === 'zoom' ? [zoomMonth] : bottomType === 'cool' ? [coolMonth] : [zoomMonth, coolMonth]
  ), [bottomType, zoomMonth, coolMonth])

  // bottom: all-time monthly (total / clinic / doctor) for the selected technique
  const bottom = useMemo(() => {
    if (!bottomMonthRows.some(Boolean)) return []
    const acc = {}
    const add = (rows) => { for (const r of rows || []) { if (r.year < 2000 || !branchEnabled.has(String(r.branch))) continue; const k = `${r.year}-${pad(r.month)}`; (acc[k] || (acc[k] = { total: 0, clinic: 0, doctor: 0 })); acc[k].total += r.priceTotal || 0; acc[k].clinic += r.priceClinic || 0; acc[k].doctor += r.priceDoctor || 0 } }
    for (const s of bottomMonthRows) add(s)
    let last = MONTH_START
    for (const s of bottomMonthRows) for (const r of s || []) { if (r.year >= 2000) { const iso = `${r.year}-${pad(r.month)}-01`; if (iso > last) last = iso } }
    return enumMonths(MONTH_START, last > today ? last : today).map(({ year, month }) => {
      const k = `${year}-${pad(month)}`
      return { label: `${TH_MON_ABBR[month - 1]} ${String(year).slice(2)}`, full: `${TH_MON_FULL[month - 1]} ${year}`, total: acc[k]?.total || 0, clinic: acc[k]?.clinic || 0, doctor: acc[k]?.doctor || 0 }
    })
  }, [bottomMonthRows, branchEnabled, today])
  const bottomSeries = [{ key: 'total', label: 'รวมทั้งหมด', color: ORANGE }, { key: 'clinic', label: 'ส่วนคลินิก', color: BLUE }, { key: 'doctor', label: 'ส่วนหมอ', color: PURPLE }]
  const bottomSum = useMemo(() => bottom.reduce((a, d) => ({ total: a.total + d.total, clinic: a.clinic + d.clinic, doctor: a.doctor + d.doctor }), { total: 0, clinic: 0, doctor: 0 }), [bottom])
  const bottomActive = bottomSeries.filter((s) => !bottomLineOff.has(s.key))

  const heatMetricKey = heatMetric === 'total' ? 'priceTotal' : heatMetric === 'clinic' ? 'priceClinic' : 'priceDoctor'
  const heatMetricColor = heatMetric === 'total' ? ORANGE : heatMetric === 'clinic' ? BLUE : PURPLE
  const heatCells = useMemo(() => {
    const acc = {}
    const add = (rows) => { for (const r of rows || []) { if (r.year < 2000 || !branchEnabled.has(String(r.branch))) continue; const k = `${r.year}-${r.month}`; acc[k] = (acc[k] || 0) + (r[heatMetricKey] || 0) } }
    for (const s of bottomMonthRows) add(s)
    return Object.entries(acc).map(([k, v]) => { const [y, m] = k.split('-').map(Number); return { year: y, month: m, value: v } })
  }, [bottomMonthRows, branchEnabled, heatMetricKey])

  const toggleBranch = (id) => setBranchOff((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })

  return (
    <>
      <p className="text-[12.5px] text-slate-500 mb-3">
        รายได้ฟอกสีฟัน (Whitening) แยกตามเทคนิค Zoom และ Cool Light · กราฟแรกแสดงแยกประเภท · กราฟรายเดือนรวมทั้งสอง
      </p>

      <div className="mb-5">
        <FilterChips label="สาขา" items={branches} enabled={branchEnabled}
          onToggle={toggleBranch} onAll={() => setBranchOff(new Set())} onClear={() => setBranchOff(new Set(branches.map((b) => b.id)))} />
      </div>

      <div className="space-y-6">
        <Card
          icon={<CalendarDays size={18} strokeWidth={1.75} />}
          title="รายได้ตามช่วงเวลา"
          badge={isRadar ? 'เรดาร์รายสาขา' : gran === 'month' ? 'รายเดือน' : 'รายวัน'}
          subtitle={isRadar ? 'Zoom และ Cool Light แยกคนละกราฟ รายสาขา' : gran === 'month' ? `ช่วงยาว (${rangeDays} วัน) — แสดงแบบรายเดือน` : 'แยกเป็นรายวัน'}
          right={<DateRange start={start} end={end} today={today} onStart={(v) => { setStart(v); setPreset('') }} onEnd={(v) => { setEnd(v); setPreset('') }} />}
        >
          <PresetBar preset={preset} onPick={applyPreset} />
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <Segmented value={metric} onChange={setMetric} options={[{ v: 'total', label: 'Total', dot: ORANGE }, { v: 'clinic', label: 'Clinic', dot: BLUE }]} />
            <span className="w-px h-5 bg-slate-200 mx-1" />
            <Toggle on={compareOn} onClick={() => setCompareOn((v) => !v)}>{compareLabel(preset)}</Toggle>
          </div>

          {rangeInvalid ? (
            <Notice text="วันสิ้นสุดต้องไม่ก่อนวันเริ่มต้น" tone="warn" />
          ) : topState.loading || !topReady || (wantCompare && (cmpState.loading || !cmpReady)) ? (
            <ChartLoading />
          ) : topState.err ? (
            <Notice text={topState.err} tone="error" />
          ) : isRadar ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-5">
              <div>
                <div className="flex flex-wrap items-end gap-x-6 gap-y-1 mb-1">
                  <Stat label={`Zoom · ${metricLabel}`} value={fmt(zoomTotal)} color={ZOOM} />
                  {wantCompare && <Stat label={comparePrevLabel(preset)} value={fmt(zoomPrevTotal)} color={SLATE} />}
                </div>
                <RadarChart axes={zoomRadar} color={ZOOM} prevColor={SLATE} compare={wantCompare} height={340} compact={false}
                  emptyText="ยังไม่มีข้อมูล Zoom ในช่วงนี้" />
              </div>
              <div>
                <div className="flex flex-wrap items-end gap-x-6 gap-y-1 mb-1">
                  <Stat label={`Cool Light · ${metricLabel}`} value={fmt(coolTotal)} color={COOL} />
                  {wantCompare && <Stat label={comparePrevLabel(preset)} value={fmt(coolPrevTotal)} color={SLATE} />}
                </div>
                <RadarChart axes={coolRadar} color={COOL} prevColor={SLATE} compare={wantCompare} height={340} compact={false}
                  emptyText="ยังไม่มีข้อมูล Cool Light ในช่วงนี้" />
              </div>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-end gap-x-8 gap-y-2 mb-3">
                <Stat label="Zoom" value={fmt(zoomTotal)} color={ZOOM} />
                <Stat label="Cool Light" value={fmt(coolTotal)} color={COOL} />
                <Stat label={`รวม · ${metricLabel}`} value={fmt(zoomTotal + coolTotal)} color={GREEN} />
                <span className="text-[12px] text-slate-400 pb-1">{topLine.data.length} {gran === 'month' ? 'เดือน' : 'วัน'}</span>
              </div>
              <RevenueChart data={topLine.data} series={topLine.series} height={310} compact={false} />
            </>
          )}
        </Card>

        <Card icon={<BarChart3 size={18} strokeWidth={1.75} />} title="รายได้รายเดือน (Zoom + Cool Light)" subtitle="ทั้งหมด ตั้งแต่เริ่มเก็บข้อมูล · รวมทั้งสองเทคนิค"
          right={<Segmented value={bottomView} onChange={setBottomView} options={[{ v: 'line', label: 'กราฟเส้น' }, { v: 'heatmap', label: 'ฮีตแมป' }]} />}>
          {monthState.loading ? (
            <ChartLoading />
          ) : monthState.err ? (
            <Notice text={monthState.err} tone="error" />
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="text-[12px] text-slate-500">เทคนิค</span>
                <Segmented value={bottomType} onChange={setBottomType} options={[{ v: 'total', label: 'รวม' }, { v: 'zoom', label: 'Zoom', dot: ZOOM }, { v: 'cool', label: 'Cool Light', dot: COOL }]} />
              </div>
              {bottomView === 'line' ? (
            <>
              <p className="text-[11.5px] text-slate-400 mb-2">คลิกที่ตัวเลขเพื่อแสดง/ซ่อนแต่ละเส้น</p>
              <div className="flex flex-wrap items-end gap-x-8 gap-y-2 mb-3">
                {bottomSeries.map((s) => {
                  const hidden = bottomLineOff.has(s.key)
                  return (
                    <button key={s.key} onClick={() => setBottomLineOff((prev) => { const n = new Set(prev); n.has(s.key) ? n.delete(s.key) : n.add(s.key); return n })}
                      className={`text-left transition-opacity ${hidden ? 'opacity-35' : ''}`}>
                      <div className="flex items-center gap-1.5 text-[12px] text-slate-500"><span className="w-2 h-2 rounded-full" style={{ background: s.color }} />{s.label}</div>
                      <div className="text-[19px] font-semibold text-slate-900 tabular-nums leading-tight">{fmt(bottomSum[s.key])}</div>
                    </button>
                  )
                })}
                <span className="text-[12px] text-slate-400 pb-1">{bottom.length} เดือน</span>
              </div>
              <RevenueChart data={bottom} series={bottomActive.length ? bottomActive : bottomSeries} height={320} labelAll valueLabels />
            </>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="text-[12px] text-slate-500">แสดง</span>
                <Segmented value={heatMetric} onChange={setHeatMetric} options={[{ v: 'total', label: 'Total', dot: ORANGE }, { v: 'clinic', label: 'Clinic', dot: BLUE }, { v: 'doctor', label: 'Doctor', dot: PURPLE }]} />
              </div>
              <HeatmapYM cells={heatCells} color={heatMetricColor} />
            </>
              )}
            </>
          )}
        </Card>
      </div>
    </>
  )
}
